import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import DEUSABI from "../abi/DEUS.json";
import ChainlinkPriceFeedABI from "../abi/ChainlinkPriceFeed.json";
import ERC20ABI from "../abi/ERC20.json";
import { FaEuroSign } from "react-icons/fa";
import { formatEther, parseUnits } from "ethers";

const deusAddress = "0x9a1EE4cC684b75eb847F2CCBDf7DeB26F8f8D6F5"; // Nová adresa kontraktu
const tokenNames = {
  "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE": "ETH",
  "0x6B175474E89094C44Da98b954EedeAC495271d0F": "DAI",
  "0xdAC17F958D2ee523a2206206994597C13D831ec7": "USDT",
  "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48": "USDC",
  "0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599": "WBTC",
  "0x514910771AF9Ca656af840dff83E8264EcF986CA": "LINK",
  "0xB8c77482e45F1F44dE1745F52C74426C631bDD52": "BNB",
  "0x7Fc66500c84A76Ad7e9c93437bFc5Ac33E2DDaE9": "AAVE",
  "0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984": "UNI",
  "0x16b6eCed5e669EAA934fB4C82F0353ab371420fa": "CUSD",
};

const DEUS = ({ provider, account, onTransaction }) => {
  const [supportedTokens, setSupportedTokens] = useState([]);
  const [tokenDecimals, setTokenDecimals] = useState({}); // Nový stav pro dynamická decimals
  const [transferFeePercent, setTransferFeePercent] = useState("0");
  const [selectedToken, setSelectedToken] = useState("");
  const [tokenAmount, setTokenAmount] = useState("");
  const [fixedPrice, setFixedPrice] = useState("0");
  const [oraclePrice, setOraclePrice] = useState("Není dostupná");
  const [priceSource, setPriceSource] = useState("Fixed");
  const [error, setError] = useState("");
  const [network, setNetwork] = useState(null);
  const [transferRecipient, setTransferRecipient] = useState("");
  const [transferAmount, setTransferAmount] = useState("");
  const [transferError, setTransferError] = useState("");

  useEffect(() => {
    const fetchNetwork = async () => {
      if (provider) {
        try {
          const networkInfo = await provider.getNetwork();
          console.log("DEUS.js - Network:", networkInfo);
          setNetwork(networkInfo);
        } catch (err) {
          console.error("DEUS.js - Error fetching network:", err);
        }
      }
    };
    fetchNetwork();
  }, [provider]);

  useEffect(() => {
    const fetchData = async () => {
      if (provider && account) {
        try {
          const signer = await provider.getSigner();
          const deusContract = new ethers.Contract(deusAddress, DEUSABI, signer);

          const [tokens, feePercent, fixedPrice, eurUsdFeedAddress] = await Promise.all([
            deusContract.getSupportedTokens(),
            deusContract.transferFeePercent(),
            deusContract.DEUS_PRICE(),
            deusContract.eurUsdFeed(),
          ]);

          console.log("DEUS.js - Supported Tokens (raw):", tokens);
          setSupportedTokens(tokens);

          // Načtení desetinných míst pro každý token
          const decimalsMap = {};
          for (const token of tokens) {
            const tokenInfo = await deusContract.supportedTokens(token);
            decimalsMap[token] = Number(tokenInfo.decimals);
          }
          setTokenDecimals(decimalsMap);

          const feePercentFormatted = (Number(feePercent) / 100).toFixed(2);
          console.log("DEUS.js - Transfer Fee Percent (formatted):", feePercentFormatted);
          setTransferFeePercent(feePercentFormatted);

          const formattedFixedPrice = formatEther(fixedPrice);
          console.log("DEUS.js - Fixed Price (formatted):", formattedFixedPrice);
          setFixedPrice(formattedFixedPrice);

          let price = "Není dostupná";
          let source = "Fixed";
          if (eurUsdFeedAddress !== ethers.ZeroAddress) {
            const chainlinkFeed = new ethers.Contract(eurUsdFeedAddress, ChainlinkPriceFeedABI, provider);
            const [latestPrice, decimals] = await Promise.all([
              chainlinkFeed.latestAnswer(),
              chainlinkFeed.decimals(),
            ]);
            console.log("DEUS.js - Chainlink Latest Price (raw):", latestPrice.toString());
            console.log("DEUS.js - Chainlink Decimals:", decimals.toString());

            const priceInUSD = Number(latestPrice) / Number(ethers.parseUnits("1", decimals));
            price = priceInUSD > 0 ? (1 / priceInUSD).toFixed(2) : "Není dostupná";
            source = priceInUSD > 0 ? "Oracle" : "Fixed";
          } else {
            price = formattedFixedPrice;
          }

          setOraclePrice(price);
          setPriceSource(source);
        } catch (err) {
          console.error("DEUS.js - Error fetching DEUS data:", err);
          setError("Failed to load DEUS data: " + (err.message || err));
        }
      }
    };
    fetchData();
  }, [provider, account]);

  const handleBuyDEUS = async () => {
    if (!provider || !account || !selectedToken || !tokenAmount) {
      setError("Please select a token and enter an amount.");
      return;
    }

    const decimals = tokenDecimals[selectedToken] || 18; // Použije dynamicky načtená decimals
    const amountInWei = parseUnits(tokenAmount, decimals);
    if (ethers.toBigInt(amountInWei) <= 0) {
      setError("Please enter a valid positive amount.");
      return;
    }

    try {
      const signer = await provider.getSigner();
      const deusContract = new ethers.Contract(deusAddress, DEUSABI, signer);

      console.log("DEUS.js - Attempting to buy DEUS with token:", selectedToken, "Amount:", tokenAmount, "Wei:", amountInWei.toString());

      // Handle approval for ERC20 tokens
      if (selectedToken !== "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE") {
        const tokenContract = new ethers.Contract(selectedToken, ERC20ABI, signer);
        const allowance = await tokenContract.allowance(account, deusAddress);
        console.log("DEUS.js - Current allowance for token:", selectedToken, "is:", allowance.toString());

        if (ethers.toBigInt(allowance) < amountInWei) {
          console.log("DEUS.js - Approving token spending...");
          const approveTx = await tokenContract.approve(deusAddress, amountInWei);
          await approveTx.wait();
          console.log("DEUS.js - Token approved for spending.");
        }
      }

      // Execute buyDEUS transaction
      const tx = await deusContract.buyDEUS(selectedToken, amountInWei, {
        value: selectedToken === "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE" ? amountInWei : 0,
      });

      const receipt = await tx.wait();
      const event = receipt.logs
        .map((log) => {
          try {
            return deusContract.interface.parseLog(log);
          } catch {
            return null;
          }
        })
        .find((e) => e?.name === "BuyDEUS");

      if (event) {
        onTransaction({
          id: receipt.transactionHash,
          type: "BuyDEUS",
          buyer: event.args.buyer,
          token: event.args.token,
          tokenAmount: formatEther(event.args.tokenAmount),
          deusAmount: formatEther(event.args.deusAmount),
          blockNumber: receipt.blockNumber,
          timestamp: (await provider.getBlock(receipt.blockNumber)).timestamp,
        });
      }

      setError("");
      setTokenAmount("");
    } catch (err) {
      console.error("DEUS.js - Error buying DEUS:", err);
      setError("Failed to buy DEUS: " + (err.reason || err.message || err));
    }
  };

  const handleTransferDEUS = async () => {
    if (!provider || !account || !transferRecipient || !transferAmount) {
      setTransferError("Please enter a recipient address and amount.");
      return;
    }

    if (!ethers.isAddress(transferRecipient)) {
      setTransferError("Invalid recipient address.");
      return;
    }

    const amount = parseFloat(transferAmount);
    if (isNaN(amount) || amount <= 0) {
      setTransferError("Please enter a valid amount.");
      return;
    }

    try {
      const signer = await provider.getSigner();
      const deusContract = new ethers.Contract(deusAddress, DEUSABI, signer);

      const amountInWei = parseUnits(transferAmount, 18); // DEUS má 18 desetinných míst
      const tx = await deusContract.transfer(transferRecipient, amountInWei);

      const receipt = await tx.wait();
      const event = receipt.logs
        .map((log) => {
          try {
            return deusContract.interface.parseLog(log);
          } catch {
            return null;
          }
        })
        .find((e) => e?.name === "Transfer");

      if (event) {
        onTransaction({
          id: receipt.transactionHash,
          type: "TransferDEUS",
          from: event.args.from,
          to: event.args.to,
          amount: formatEther(event.args.value),
          blockNumber: receipt.blockNumber,
          timestamp: (await provider.getBlock(receipt.blockNumber)).timestamp,
        });
      }

      setTransferError("");
      setTransferRecipient("");
      setTransferAmount("");
    } catch (err) {
      console.error("DEUS.js - Error transferring DEUS:", err);
      setTransferError("Failed to transfer DEUS: " + (err.reason || err.message));
    }
  };

  return (
    <div className="deus">
      <h2 className="deus">
        <FaEuroSign className="icon" /> DEUS
      </h2>
      {/* Sekce Buy DEUS */}
      <div className="action-box deus">
        <h3 className="deus">Buy DEUS</h3>
        <p className="deus">Transfer Fee: {transferFeePercent}%</p>
        <p className="deus">Fixed Price: {fixedPrice} EUR</p>
        <p className="deus">
          Current Price (USD/EUR): {oraclePrice}{" "}
          {oraclePrice !== "Není dostupná" ? "EUR" : ""} (Source: {priceSource})
        </p>
        {network && (
          <p className="deus">
            Connected Network: {network.name} (Chain ID: {network.chainId.toString()})
          </p>
        )}
        <div>
          <label className="deus">Select Token:</label>
          <select
            className="deus"
            value={selectedToken}
            onChange={(e) => setSelectedToken(e.target.value)}
          >
            <option value="" disabled>Select a token</option>
            {supportedTokens.map((token) => (
              <option key={token} value={token}>
                {tokenNames[token] || token} ({tokenDecimals[token] || 18} decimals)
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="deus">Token Amount:</label>
          <input
            className="deus"
            type="text"
            value={tokenAmount}
            onChange={(e) => setTokenAmount(e.target.value)}
            placeholder="Enter amount (e.g., 1, 0.1, 0.001)"
          />
        </div>
        <button className="deus" onClick={handleBuyDEUS}>
          Buy DEUS
        </button>
        {error && <p className="deus">{error}</p>}
      </div>

      {/* Sekce Transfer DEUS */}
      <div className="action-box deus">
        <h3 className="deus">Transfer DEUS</h3>
        <div>
          <label className="deus">Recipient Address:</label>
          <input
            className="deus"
            type="text"
            value={transferRecipient}
            onChange={(e) => setTransferRecipient(e.target.value)}
            placeholder="Enter recipient address"
          />
        </div>
        <div>
          <label className="deus">Amount to Transfer:</label>
          <input
            className="deus"
            type="text"
            value={transferAmount}
            onChange={(e) => setTransferAmount(e.target.value)}
            placeholder="Enter amount"
          />
        </div>
        <button className="deus" onClick={handleTransferDEUS}>
          Transfer DEUS
        </button>
        {transferError && <p className="deus">{transferError}</p>}
      </div>
    </div>
  );
};

export default DEUS;